import { FolderOpenIcon } from '@heroicons/react/24/outline';
import { FolderIcon as FolderIconSolid } from '@heroicons/react/24/solid';
import React from 'react';

import { CollectionNode } from '@/services/types/collection-types';

import { treeStyles, TreeViewSize } from '../constants/treeStyles';
import { handleEventPropagation, isClickOnElement } from '../utils/nodeUtils';
import { BaseNodeItem } from './BaseNodeItem';
import { FolderActions } from './FolderActions';

interface FolderNodeItemProps {
  item: CollectionNode;
  level: number;
  isExpanded: boolean;
  hasContent: boolean;
  totalFilesCount: number;
  isAllSelected: boolean;
  isSelected: boolean;
  isRoot?: boolean;
  rootNodeId?: string;
  className?: string;
  matchesSearch?: boolean;
  isTargeted?: boolean;
  isDraggingActive?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSuccess?: () => void;
  onSelect: () => void;
  onToggleExpand: () => void;
  onSelectAll: (e: React.MouseEvent) => void;
  isFolderSelected?: boolean;
  onToggleFolderSelection?: () => void;
  'aria-label'?: string;
  size?: TreeViewSize;
}

interface FolderStatsProps {
  totalFilesCount: number;
  size?: TreeViewSize;
}

const FolderStats: React.FC<FolderStatsProps> = ({
  totalFilesCount,
  size = 'sm',
}) => (
  <span className={`${treeStyles.stats[size]} text-muted-foreground mr-1`}>
    ({totalFilesCount})
  </span>
);

interface FolderActionsComponentProps {
  node: CollectionNode;
  isRoot: boolean;
  rootNodeId: string;
  onSuccess: () => void;
  size?: TreeViewSize;
}

const FolderActionsComponent: React.FC<FolderActionsComponentProps> = ({
  node,
  isRoot,
  rootNodeId,
  onSuccess,
  size = 'sm',
}) => (
  <FolderActions
    node={node}
    isRoot={isRoot}
    rootNodeId={rootNodeId}
    onSuccess={onSuccess}
    size={size}
  />
);

export const FolderNodeItem: React.FC<FolderNodeItemProps> = ({
  item: node,
  level,
  isExpanded,
  hasContent,
  totalFilesCount,
  isAllSelected,
  isSelected,
  isRoot = false,
  rootNodeId,
  className,
  matchesSearch = false,
  isTargeted = false,
  isDraggingActive = false,
  onMouseEnter,
  onMouseLeave,
  onSuccess,
  onSelect,
  onToggleExpand,
  onSelectAll,
  isFolderSelected = false,
  onToggleFolderSelection,
  'aria-label': ariaLabel,
  size = 'sm',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    // Only handle folder selection if we're not interacting with actions
    if (
      !isClickOnElement(e, 'folder-actions') &&
      !isClickOnElement(e, 'selection-indicator')
    ) {
      onSelect();
      // Toggle folder expansion if it has content
      onToggleExpand();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle keyboard selection
    handleEventPropagation.keyboardSelect(e, onSelect);
  };

  // Ensure onSuccess is always a function
  const safeOnSuccess = () => {
    if (onSuccess) onSuccess();
  };

  const handleCheckboxChange = () => {
    if (!isRoot && onToggleFolderSelection) {
      onToggleFolderSelection();
    } else {
      onSelectAll({ stopPropagation: () => {} } as React.MouseEvent);
    }
  };

  // Get the appropriate folder icon based on state
  const folderIcon = isExpanded ? (
    <FolderOpenIcon
      className={`${treeStyles.icons.folderOpen} text-blue-400 flex-shrink-0`}
    />
  ) : (
    <FolderIconSolid
      className={`${treeStyles.icons.folder} ${
        hasContent ? 'text-blue-400' : 'text-blue-400/50'
      } flex-shrink-0`}
    />
  );

  // Pre-define stats renderer
  const stats = hasContent ? (
    <FolderStats totalFilesCount={totalFilesCount} size={size} />
  ) : null;

  // Pre-define actions renderer
  const actions = (
    <FolderActionsComponent
      node={node}
      isRoot={isRoot}
      rootNodeId={rootNodeId || node.id}
      onSuccess={safeOnSuccess}
      size={size}
    />
  );

  return (
    <BaseNodeItem
      level={level}
      size={size}
      className={`${className} ${matchesSearch ? 'font-semibold' : ''} ${
        isTargeted && isDraggingActive
          ? 'bg-blue-50 ring-1 ring-blue-300 transition-colors animate-pulse'
          : ''
      }`}
      isSelected={isSelected}
      icon={folderIcon}
      label={isRoot ? 'All Files' : node.name}
      stats={stats}
      actions={actions}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel || `Folder: ${isRoot ? 'All Files' : node.name}`}
      aria-expanded={hasContent ? isExpanded : undefined}
      selectable={hasContent || (!isRoot && !!onToggleFolderSelection)}
      selectionChecked={!isRoot && onToggleFolderSelection ? isFolderSelected : isAllSelected}
      onSelectionChange={handleCheckboxChange}
      dataCollectionId={node.id}
    />
  );
};
